import { useState } from 'react';
import {
    X, Edit, Heart, MapPin, Package, Tag, Eye, Clock, User, Building2,
    ExternalLink, Scale, Ruler, Hash, Truck, Store, ChevronLeft, ChevronRight
} from 'lucide-react';
import { getFileUrl } from '../../services/filesService';
import { PRODUCT_STATUS, LISTING_TYPE, PRODUCT_CONDITION, PICKUP_TYPE, OWNER_TYPE } from '../../services/productsService';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Product Detail Component
 * Displays full product information
 */
const ProductDetail = ({
    product,
    isOwner = false,
    isFavorite = false,
    onClose,
    onEdit,
    onToggleFavorite
}) => {
    const { language, t } = useLanguage();
    const tp = t.products || {};
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

    // Get photos
    const photos = product.photos || [];
    const hasPhotos = photos.length > 0;

    const getPhotoUrl = (photo) => {
        const filePath = photo.filePath || photo.filepath || photo.FILEPATH;
        return filePath ? getFileUrl(filePath) : null;
    };

    // Format price
    const formatPrice = () => {
        if (product.listingType === LISTING_TYPE.GIVE || !product.price) {
            return tp.free;
        }
        return new Intl.NumberFormat(language === 'fr' ? 'fr-BE' : 'en-US', {
            style: 'currency',
            currency: product.currency || 'EUR'
        }).format(product.price);
    };

    // Format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString(language === 'fr' ? 'fr-BE' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Get pickup type label
    const getPickupLabel = () => {
        switch (product.pickupType) {
            case PICKUP_TYPE.ON_SITE: return tp.onSite;
            case PICKUP_TYPE.IN_STORE: return tp.inStore;
            case PICKUP_TYPE.BOTH: return tp.both;
            case PICKUP_TYPE.SHIPPING: return tp.shipping;
            default: return tp.onSite;
        }
    };

    // Navigate photos
    const nextPhoto = () => {
        setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
    };

    const prevPhoto = () => {
        setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
    };

    // Get status badge
    const getStatusBadge = () => {
        if (product.status === PRODUCT_STATUS.SOLD) {
            return { text: tp.sold, color: 'bg-red-500' };
        }
        if (product.status === PRODUCT_STATUS.RESERVED) {
            return { text: tp.reserved, color: 'bg-yellow-500' };
        }
        if (product.status === PRODUCT_STATUS.INACTIVE) {
            return { text: tp.inactive, color: 'bg-gray-500' };
        }
        return null;
    };

    const statusBadge = getStatusBadge();
    const createdAt = product.createdAt || product.createdat || product.CREATEDAT;

    return (
        <div className="relative">
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 bg-white border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 truncate pr-4">
                    {product.title}
                </h2>
                <div className="flex items-center gap-2">
                    {isOwner && onEdit && (
                        <button
                            onClick={onEdit}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-cabaza-purple bg-cabaza-purple/10 rounded-lg hover:bg-cabaza-purple/20 transition-colors"
                        >
                            <Edit className="w-4 h-4" />
                            {tp.edit}
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            <div className="p-4 md:p-6">
                <div className="grid md:grid-cols-2 gap-6">
                    {/* Photos */}
                    <div className="space-y-4">
                        {/* Main photo */}
                        <div className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden">
                            {hasPhotos ? (
                                <>
                                    <img
                                        src={getPhotoUrl(photos[currentPhotoIndex])}
                                        alt={product.title}
                                        className="w-full h-full object-cover"
                                    />

                                    {/* Status badge */}
                                    {statusBadge && (
                                        <div className={`absolute top-4 left-4 ${statusBadge.color} text-white text-sm font-semibold px-3 py-1 rounded-full`}>
                                            {statusBadge.text}
                                        </div>
                                    )}

                                    {/* Navigation arrows */}
                                    {photos.length > 1 && (
                                        <>
                                            <button
                                                onClick={prevPhoto}
                                                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-md hover:bg-white transition-colors"
                                            >
                                                <ChevronLeft className="w-5 h-5 text-gray-700" />
                                            </button>
                                            <button
                                                onClick={nextPhoto}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-md hover:bg-white transition-colors"
                                            >
                                                <ChevronRight className="w-5 h-5 text-gray-700" />
                                            </button>
                                        </>
                                    )}

                                    {/* Photo counter */}
                                    {photos.length > 1 && (
                                        <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/50 text-white text-sm rounded-full">
                                            {currentPhotoIndex + 1} / {photos.length}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <Package className="w-16 h-16 text-gray-300" />
                                </div>
                            )}
                        </div>

                        {/* Thumbnails */}
                        {photos.length > 1 && (
                            <div className="flex gap-2 overflow-x-auto pb-2">
                                {photos.map((photo, index) => {
                                    const photoId = photo.id || photo.ID;
                                    return (
                                        <button
                                            key={photoId}
                                            onClick={() => setCurrentPhotoIndex(index)}
                                            className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                                                index === currentPhotoIndex
                                                    ? 'border-cabaza-purple'
                                                    : 'border-transparent'
                                            }`}
                                        >
                                            <img
                                                src={getPhotoUrl(photo)}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Info */}
                    <div className="space-y-6">
                        {/* Price and type */}
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <span className={`text-3xl font-bold ${
                                    product.listingType === LISTING_TYPE.GIVE ? 'text-green-600' : 'text-cabaza-purple'
                                }`}>
                                    {formatPrice()}
                                </span>
                                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                    product.listingType === LISTING_TYPE.GIVE
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-cabaza-purple/10 text-cabaza-purple'
                                }`}>
                                    {product.listingType === LISTING_TYPE.GIVE ? tp.toGive : tp.toSell}
                                </span>
                            </div>
                            {product.priceNegotiable && product.listingType !== LISTING_TYPE.GIVE && (
                                <p className="text-sm text-gray-500">{tp.negotiable}</p>
                            )}
                        </div>

                        {/* Category and condition */}
                        <div className="flex flex-wrap gap-2">
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-700">
                                <Tag className="w-4 h-4" />
                                {product.typeName || product.typename || product.TYPENAME}
                            </div>
                            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                                product.condition === PRODUCT_CONDITION.NEW
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-700'
                            }`}>
                                {product.condition === PRODUCT_CONDITION.NEW ? tp.new : tp.used}
                            </div>
                        </div>

                        {/* Description */}
                        {product.description && (
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-2">{tp.description}</h3>
                                <p className="text-gray-600 whitespace-pre-wrap">{product.description}</p>
                            </div>
                        )}

                        {/* Details */}
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-3">{tp.details}</h3>
                            <div className="grid grid-cols-2 gap-3">
                                {product.quantity > 1 && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <Hash className="w-4 h-4 text-gray-400" />
                                        <span>{tp.quantity}: {product.quantity}</span>
                                    </div>
                                )}
                                {product.weight && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <Scale className="w-4 h-4 text-gray-400" />
                                        <span>{tp.weight}: {product.weight} kg</span>
                                    </div>
                                )}
                                {(product.dimensionLength || product.dimensionWidth || product.dimensionHeight) && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600 col-span-2">
                                        <Ruler className="w-4 h-4 text-gray-400" />
                                        <span>
                                            {tp.dimensions}: {product.dimensionLength || '-'} × {product.dimensionWidth || '-'} × {product.dimensionHeight || '-'} cm
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Pickup */}
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-3">{tp.pickup}</h3>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    {product.pickupType === PICKUP_TYPE.IN_STORE ? (
                                        <Store className="w-4 h-4 text-gray-400" />
                                    ) : (
                                        <MapPin className="w-4 h-4 text-gray-400" />
                                    )}
                                    <span>{getPickupLabel()}</span>
                                </div>
                                {product.pickupAddress && (
                                    <p className="text-sm text-gray-500 ml-6">{product.pickupAddress}</p>
                                )}
                                {product.shippingAvailable && (
                                    <div className="flex items-center gap-2 text-sm text-green-600">
                                        <Truck className="w-4 h-4" />
                                        <span>{tp.shippingAvailable}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Seller info */}
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-3">{tp.seller}</h3>
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-full ${
                                    product.ownerType === OWNER_TYPE.BUSINESS
                                        ? 'bg-blue-100'
                                        : 'bg-gray-100'
                                }`}>
                                    {product.ownerType === OWNER_TYPE.BUSINESS ? (
                                        <Building2 className="w-5 h-5 text-blue-600" />
                                    ) : (
                                        <User className="w-5 h-5 text-gray-600" />
                                    )}
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900">
                                        {product.placeName || product.sellerName || product.SELLERNAME || 'Vendeur'}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        {product.ownerType === OWNER_TYPE.BUSINESS ? tp.business : tp.personal}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* External URL */}
                        {product.externalUrl && (
                            <a
                                href={product.externalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-cabaza-purple hover:underline"
                            >
                                <ExternalLink className="w-4 h-4" />
                                {tp.externalLink}
                            </a>
                        )}

                        {/* Stats */}
                        <div className="flex items-center gap-4 pt-4 border-t border-gray-100 text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                <span>{product.viewCount || 0} {tp.views}</span>
                            </div>
                            {createdAt && (
                                <div className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    <span>{tp.postedOn} {formatDate(createdAt)}</span>
                                </div>
                            )}
                        </div>

                        {/* Favorite button for non-owners */}
                        {!isOwner && onToggleFavorite && (
                            <button
                                onClick={() => onToggleFavorite(product)}
                                className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold transition-colors ${
                                    isFavorite
                                        ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500' : ''}`} />
                                {isFavorite ? tp.removeFromFavorites : tp.addToFavorites}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetail;
